import api from './api';

/**
 * Suricata IDS/IPS Service
 *
 * API client for Suricata configuration and alert management
 */

export interface SuricataConfig {
  id?: number;
  edge_id: string;
  tenant_id?: string;
  enabled: boolean;
  mode: 'ids' | 'ips';
  threads: number;
  af_xdp_mode: 'drv' | 'skb';
  busy_poll_enabled: boolean;
  detect_profile: 'low' | 'medium' | 'high' | 'custom';
  home_net: string[];
  rule_sources: string[];
  custom_rules?: string;
  rule_update_enabled: boolean;
  alert_threshold: 'all' | 'medium' | 'high' | 'critical';
  block_severity: 'none' | 'critical' | 'high' | 'medium';
  // Service Chain Settings
  service_chain_enabled: boolean;
  service_chain_mode: 'tap-afxdp' | 'inline' | 'bypass';
  traffic_selector: 'all' | 'wan' | 'lan' | 'custom';
  fallback_action: 'forward' | 'drop';
  created_at?: string;
  updated_at?: string;
}

export interface SuricataAlert {
  id: number;
  edge_id: string;
  tenant_id: string;
  signature_id: number;
  signature: string;
  category: string;
  severity: number;
  src_ip: string;
  dst_ip: string;
  src_port: number;
  dst_port: number;
  protocol: string;
  action: 'allowed' | 'blocked' | 'dropped';
  timestamp: string;
  received_at: string;
}

export interface AlertSummary {
  total_alerts: number;
  blocked_count: number;
  allowed_count: number;
  high_severity: number;
  medium_severity: number;
  low_severity: number;
  by_category: { category: string; count: number }[];
  recent_signatures: { signature: string; count: number }[];
}

export interface SuricataStatus {
  edge_id: string;
  enabled: boolean;
  mode: string;
  running: boolean;
  uptime_seconds?: number;
  packets_received?: number;
  packets_dropped?: number;
  alerts_total?: number;
  alerts_blocked?: number;
  last_update?: string;
}

export interface SuricataConfigResponse {
  edge_id: string;
  config: SuricataConfig | null;
}

export interface SuricataConfigUpdateResponse {
  edge_id: string;
  config: SuricataConfig;
  message: string;
}

export interface SuricataAlertsResponse {
  edge_id: string;
  alerts: SuricataAlert[];
  total: number;
}

export interface SuricataAlertSummaryResponse {
  edge_id: string;
  hours_back: number;
  summary: AlertSummary;
}

export interface SuricataStatusResponse {
  edge_id: string;
  status: SuricataStatus;
}

/**
 * Transform API response (nested format) to frontend SuricataConfig (flat format)
 */
function transformApiToConfig(apiData: any): SuricataConfig {
  return {
    id: apiData.id,
    edge_id: apiData.edge_id,
    tenant_id: apiData.tenant_id,
    enabled: apiData.enabled,
    mode: apiData.mode || 'ips',
    threads: apiData.threads || 2,
    af_xdp_mode: apiData.af_xdp?.mode || 'drv',
    busy_poll_enabled: apiData.af_xdp?.busy_poll_enabled ?? true,
    detect_profile: apiData.detection?.profile || 'medium',
    home_net: apiData.detection?.home_net || ['192.168.0.0/16', '10.0.0.0/8', '172.16.0.0/12'],
    rule_sources: apiData.rules?.sources || ['et/open'],
    custom_rules: apiData.rules?.custom_rules || '',
    rule_update_enabled: apiData.rules?.update_enabled ?? true,
    alert_threshold: apiData.alerting?.threshold || 'high',
    block_severity: apiData.alerting?.block_severity || 'critical',
    // Service Chain Settings
    service_chain_enabled: apiData.service_chain?.enabled ?? true,
    service_chain_mode: apiData.service_chain?.chain_mode || 'tap-afxdp',
    traffic_selector: apiData.service_chain?.traffic_selector || 'all',
    fallback_action: apiData.service_chain?.fallback_action || 'forward',
    created_at: apiData.created_at,
    updated_at: apiData.updated_at,
  };
}

const suricataService = {
  /**
   * Get Suricata configuration for an edge
   * Uses /api/v2/ endpoint
   */
  getConfig: async (edgeId: string): Promise<SuricataConfigResponse> => {
    const response = await api.get(`/api/v2/edges/${edgeId}/suricata/config`);
    // Note: api.ts interceptor returns response.data directly
    // So response here is { success: true, data: { configured, enabled, ...nested } }
    const apiData = (response as any).data;

    if (!apiData || !apiData.configured) {
      return { edge_id: edgeId, config: null };
    }

    return {
      edge_id: apiData.edge_id || edgeId,
      config: transformApiToConfig(apiData),
    };
  },

  /**
   * Update Suricata configuration
   * Uses /api/v2/ endpoint
   */
  updateConfig: async (edgeId: string, config: Partial<SuricataConfig>): Promise<SuricataConfigUpdateResponse> => {
    const response = await api.put(`/api/v2/edges/${edgeId}/suricata/config`, config);
    // Note: api.ts interceptor returns response.data directly
    const apiData = (response as any).data;

    return {
      edge_id: apiData.edge_id || edgeId,
      config: transformApiToConfig(apiData),
      message: apiData.message || 'Configuration updated',
    };
  },

  /**
   * Get recent alerts for an edge
   * Uses /api/v2/ endpoint
   */
  getAlerts: async (edgeId: string, limit: number = 100): Promise<SuricataAlertsResponse> => {
    const response = await api.get(`/api/v2/edges/${edgeId}/suricata/alerts`, {
      params: { limit },
    });
    // API returns { success, data: { items, pagination, filters } }
    const apiData = (response as any).data;
    return {
      edge_id: edgeId,
      alerts: apiData?.items || apiData?.data?.items || [],
      total: apiData?.pagination?.count || apiData?.data?.pagination?.count || 0,
    };
  },

  /**
   * Get alert summary with statistics
   * Uses /api/v2/ endpoint
   */
  getAlertSummary: async (edgeId: string, hoursBack: number = 24): Promise<SuricataAlertSummaryResponse> => {
    const response = await api.get(`/api/v2/edges/${edgeId}/suricata/alerts/summary`, {
      params: { hours: hoursBack },
    });
    // API returns { success, data: { total, by_severity, by_category, by_action, time_range } }
    const apiData = (response as any).data;
    const raw = apiData?.data || apiData;

    // Map API shape to frontend AlertSummary shape
    const bySeverity = raw?.by_severity || [];
    const byAction = raw?.by_action || [];
    const byCategory = raw?.by_category || [];

    const severityCount = (sev: number) => {
      const found = bySeverity.find((s: any) => s.severity === sev);
      return found ? Number(found.count) : 0;
    };
    const actionCount = (act: string) => {
      const found = byAction.find((a: any) => a.action === act);
      return found ? Number(found.count) : 0;
    };

    return {
      edge_id: edgeId,
      hours_back: hoursBack,
      summary: {
        total_alerts: Number(raw?.total || 0),
        blocked_count: actionCount('blocked') + actionCount('dropped'),
        allowed_count: actionCount('allowed'),
        high_severity: severityCount(1),
        medium_severity: severityCount(2),
        low_severity: severityCount(3),
        by_category: byCategory.map((c: any) => ({ category: c.category, count: Number(c.count) })),
        recent_signatures: [],
      },
    };
  },

  /**
   * Get Suricata status (running state, metrics)
   * Uses /api/v2/ endpoint
   */
  getStatus: async (edgeId: string): Promise<SuricataStatusResponse> => {
    const response = await api.get(`/api/v2/edges/${edgeId}/suricata/status`);
    // Note: api.ts interceptor returns response.data directly
    // So response here is { success: true, data: { enabled, running, ... } }
    const apiData = (response as any).data;

    if (!apiData) {
      return {
        edge_id: edgeId,
        status: {
          edge_id: edgeId,
          enabled: false,
          mode: 'ips',
          running: false,
        },
      };
    }

    return {
      edge_id: apiData.edge_id || edgeId,
      status: {
        edge_id: apiData.edge_id || edgeId,
        enabled: apiData.enabled ?? false,
        mode: apiData.mode || 'ips',
        running: apiData.running ?? false,
        uptime_seconds: apiData.uptime_seconds,
        packets_received: apiData.packets_received,
        packets_dropped: apiData.packets_dropped,
        alerts_total: apiData.alerts_total,
        alerts_blocked: apiData.alerts_blocked,
        last_update: apiData.last_update,
      },
    };
  },
};

export default suricataService;
